/* Shared asset → BS-line picker options.
 *
 * After the Cp1/Cp2 per-asset-routing work, every fixed/intangible asset carries an EXPLICIT
 * bs_line_id (the BS leaf it rolls onto). Any UI that creates/edits an asset must offer the
 * tenant's BS asset leaves as the picker options — sourced ONE way, not re-derived per page.
 *
 * Two source shapes:
 *   - Engine-ready pages (DAModulePage, ScenariosPage) have rawData.bsLines (carry `role`) plus
 *     rawData.bsActualsMeta (labels) → buildAssetBsLineOptions(bsLines, bsActualsMeta).
 *   - The onboarding wizard (AdminPage) runs BEFORE bsLines are built; it has only
 *     bs_actuals_meta (which carries both role + label) → assetBsLineOptionsFromMeta(meta).
 *
 * Both return { fa: [{id,label}], ia: [{id,label}] } filtered by role (fixed_asset /
 * intangible_asset). NO Semitek fallback, NO keyword guessing — an empty list means the tenant's
 * BS chart has no asset lines of that role yet (a configure-first condition, not a default).
 */
export function buildAssetBsLineOptions(bsLines, bsActualsMeta) {
  const meta = bsActualsMeta || {};
  const bl = bsLines || [];
  const forRole = role => bl.filter(l => l.role === role).map(l => ({ id: l.id, label: meta[l.id]?.label || l.id }));
  return { fa: forRole('fixed_asset'), ia: forRole('intangible_asset') };
}

export function assetBsLineOptionsFromMeta(bsActualsMeta) {
  const meta = bsActualsMeta || {};
  const fa = [];
  const ia = [];
  for (const [id, m] of Object.entries(meta)) {
    if (!m || !m.label) continue;
    if (m.role === 'fixed_asset') fa.push({ id, label: m.label });
    else if (m.role === 'intangible_asset') ia.push({ id, label: m.label });
  }
  return { fa, ia };
}

// id → label lookup from a {fa,ia} options object (for read-only display of a stored bs_line_id).
export function bsLineLabelMap(options) {
  const m = {};
  [...(options?.fa || []), ...(options?.ia || [])].forEach(l => { m[l.id] = l.label; });
  return m;
}

/* ── Deferred-revenue line options (Step-7 rmm.bs_line_key picker — C-1) ──────────
 * Same read-bs_actuals_meta-by-role pattern as the asset options above (this wizard
 * step runs BEFORE the live model exists, so it reads bs_actuals_meta, which carries
 * both role + label). A revenue module (ae_quota / company_quota) must name the BS
 * deferred-revenue leaf its balance rolls onto — buildBSLines keys the module-DR
 * wiring on rmm.bs_line_key, and a NULL silently drops that wiring (C-1).
 *
 * Filters role === 'deferred_revenue' → EXCLUDES 'Deferred Income Tax' (role=accrued,
 * a decoy). NO keyword guessing, NO fallback — an empty list means the tenant's BS
 * chart has no deferred-revenue leaf, so the operator uses the "Synthetic (forecast-
 * only)" option (buildBSLines emits a synthetic when bs_line_key is not in meta). */
export function deferredRevenueOptionsFromMeta(bsActualsMeta) {
  const meta = bsActualsMeta || {};
  const out = [];
  for (const [id, m] of Object.entries(meta)) {
    if (!m || !m.label) continue;
    if (m.role === 'deferred_revenue') out.push({ id, label: m.label });
  }
  return out;
}

// W-010 (2026-09-24): suggestDeferredRevenueLine and its private _drTokens / _DR_STOPWORDS are DELETED. They pre-filled a
// DR module's bs_line_key by shared words, and the pick rendered exactly like an operator's choice (a guess presented as a
// decision -- the section E1 defect). Step 7 now starts every unsaved DR row blank and Required.
